import React from "react";
import "./App.scss";
import LayoutFM from "./components/layouts";
import { ConfigProvider, App as AntdApp } from "antd";
import { Outlet, Route, Routes } from "react-router-dom";
import LoginPage from "./pages/login";
import ProtectedRoute from "./routers/ProtectedRoute";
import OauthCallback from "./pages/login/oauthCallback";
import { MessageProvider } from "./context/MessageContext";
import PermissionPage from "./pages/permission";
import AccessDenied from "./pages/403";
import SupplierPage from "./pages/supplier";
import CreateAndEditSupplierPage from "./pages/supplier/create-and-edit.supplier";
import SystemDefaultPage from "./pages/system-default/system-default";
import BookingSupplierCostPage from "./pages/booking-supplier-cost";
import ExpectingSupplierPaymentPage from "./pages/expect-supplier-payment";
import InvoicePage from "./pages/invoice";
import SupplierInvoiceRecordPage from "./pages/invoice/supplier-invoice-record";
import EspAudiTrailPage from "./pages/expect-supplier-payment/audit-trail";
import { SupplierStoreProvider } from "./context/SupplierContext";
import { PageStoreProvider } from "./context/PageStoreContext";
import InvoicePageDebug from "./pages/invoice-debug";
import InvoiceAudiTrailPage from "./pages/invoice/audit-trail";
import SIRAudiTrailPage from "./pages/invoice/audit-trail-sir";
import SupplierInvoiceRecordFlightPage from "./pages/invoice/supplier-invoice-record-flight";
import MatchingDetailPage from "./pages/workflow/matching-detail";
import ApWorkflowPage from "./pages/workflow/ap-workflow";
import WorkflowAudiTrailPage from "./pages/workflow/audit-trail";
import FlightWorkflowPage from "./pages/workflow/flight-workflow";
import TourEventOrOtherWorkflowPage from "./pages/workflow/tour-event-or-other-workflow";
import { WorkflowTypeEnum } from "./constants/enum";
import DashboardPage from "./pages/dashboard";
import TourEventOtherWorkflowDetailPage from "./pages/workflow/tour-event-other-workflow-detail";
import MasterDataPage from "./pages/settings/master-data";
import SupplierAudiTrailPage from "./pages/supplier/audit-trail";
import ReportPage from "./pages/report";

const underConstructionRoutes = [
  {
    path: "/system-default",
    permission: "SYSTEM_DEFAULT",
    label: <SystemDefaultPage />,
  },
];

const App: React.FC = () => {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorSuccess: "#12ba98",
          colorWarning: "#fa8c16",
          colorError: "#ff4d4f",
          colorPrimary: "#0088CC",
          colorLink: "#0088CC",
          borderRadius: 3,
          fontFamily: "Lato",
          fontSize: 16,
        },
        components: {
          Button: {
            controlHeightSM: 32,
            controlHeight: 42,
            controlHeightLG: 60,
            contentFontSizeLG: 26,
          },
        },
      }}
    >
      <AntdApp>
        <MessageProvider>
          <PageStoreProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/oauth2/callback" element={<OauthCallback />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <LayoutFM>
                      <Outlet />
                    </LayoutFM>
                  </ProtectedRoute>
                }
              >
                <Route
                  path="/permission"
                  element={
                    <ProtectedRoute permissions={["PERMISSION"]}>
                      <PermissionPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/supplier"
                  element={
                    <ProtectedRoute permissions={["SUPPLIER"]}>
                      <SupplierStoreProvider>
                        <Outlet />
                      </SupplierStoreProvider>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<SupplierPage />} />
                  <Route path="create" element={<CreateAndEditSupplierPage />} />
                  <Route path=":id" element={<CreateAndEditSupplierPage />} />
                  <Route path=":id/audit-trail" element={<SupplierAudiTrailPage />} />
                </Route>
                <Route
                  path="/booking-supplier-cost"
                  element={
                    <ProtectedRoute permissions={["BOOKING_SUPPLIER_COST"]}>
                      <BookingSupplierCostPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/expect-supplier-payment"
                  element={
                    <ProtectedRoute permissions={["BOOKING_SUPPLIER_COST"]}>
                      <Outlet />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<ExpectingSupplierPaymentPage />} />
                  <Route path=":id/audit-trail" element={<EspAudiTrailPage />} />
                </Route>

                <Route
                  path="/invoice"
                  element={
                    <ProtectedRoute permissions={["INVOICE"]}>
                      <Outlet />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<InvoicePage />} />
                  <Route path=":id/audit-trail" element={<InvoiceAudiTrailPage />} />
                </Route>

                <Route
                  path="/invoice-debug"
                  element={
                    <ProtectedRoute permissions={["INVOICE"]}>
                      <Outlet />
                    </ProtectedRoute>
                  }>
                  <Route index element={<InvoicePageDebug />} />
                  <Route path=":id/audit-trail" element={<InvoiceAudiTrailPage />} />
                </Route>

                <Route
                  path="/supplier-invoice-record"
                  element={
                    <ProtectedRoute permissions={["INVOICE"]}>
                      <Outlet />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<SupplierInvoiceRecordPage />} />
                  <Route path=":id/audit-trail" element={<SIRAudiTrailPage />} />
                </Route>

                <Route
                  path="/supplier-invoice-record-flight"
                  element={
                    <ProtectedRoute permissions={["INVOICE"]}>
                      <Outlet />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<SupplierInvoiceRecordFlightPage />} />
                  <Route path=":id/audit-trail" element={<SIRAudiTrailPage />} />
                </Route>

                <Route
                  path="/master-data"
                  element={
                    <ProtectedRoute permissions={["SYSTEM_DEFAULT"]}>
                      <MasterDataPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/ap-workflow"
                  element={
                    <ProtectedRoute permissions={["WORKFLOW"]}>
                      <Outlet />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<ApWorkflowPage />} />
                  <Route
                    path=":id"
                    element={
                      <ProtectedRoute permissions={["BOOKING_SUPPLIER_COST", "INVOICE"]}>
                        <MatchingDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path=":id/audit-trail" element={<WorkflowAudiTrailPage />} />
                </Route>

                <Route
                  path="/flight-workflow"
                  element={
                    <ProtectedRoute permissions={["WORKFLOW"]}>
                      <Outlet />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<FlightWorkflowPage />} />
                  <Route
                    path=":id"
                    element={
                      <ProtectedRoute permissions={["BOOKING_SUPPLIER_COST", "INVOICE"]}>
                        <MatchingDetailPage isTypeFlight={true} />
                      </ProtectedRoute>
                    }
                  />
                  <Route index element={<FlightWorkflowPage />} />
                  <Route path=":id/audit-trail" element={<WorkflowAudiTrailPage />} />
                </Route>

                <Route
                  path="/tour-event-workflow"
                  element={
                    <ProtectedRoute permissions={["WORKFLOW"]}>
                      <Outlet />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<TourEventOrOtherWorkflowPage type={WorkflowTypeEnum.TourEvent} />} />
                  <Route
                    path=":id"
                    element={
                      <ProtectedRoute permissions={["BOOKING_SUPPLIER_COST"]}>
                        <TourEventOtherWorkflowDetailPage type={WorkflowTypeEnum.TourEvent}  />
                      </ProtectedRoute>
                    }
                  />
                  <Route index element={<FlightWorkflowPage />} />
                  <Route path=":id/audit-trail" element={<WorkflowAudiTrailPage type="esp_workflow" />} />
                </Route>

                <Route
                  path="/other-workflow"
                  element={
                    <ProtectedRoute permissions={["WORKFLOW"]}>
                      <Outlet />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<TourEventOrOtherWorkflowPage type={WorkflowTypeEnum.Other} />} />
                  <Route
                    path=":id"
                    element={
                      <ProtectedRoute permissions={["BOOKING_SUPPLIER_COST"]}>
                        <TourEventOtherWorkflowDetailPage type={WorkflowTypeEnum.Other}  />
                      </ProtectedRoute>
                    }
                  />
                  <Route index element={<FlightWorkflowPage />} />
                  <Route path=":id/audit-trail" element={<WorkflowAudiTrailPage type="esp_workflow" />} />
                </Route>

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute permissions={["DASHBOARD"]}>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/financial-reporting"
                  element={
                    <ProtectedRoute permissions={["FINANCIAL_REPORT"]}>
                      <ReportPage />
                    </ProtectedRoute>
                  }
                />

                {underConstructionRoutes.map(({ path, permission, label }) => (
                  <Route
                    key={path}
                    path={path}
                    element={<ProtectedRoute permissions={[permission]}>{label}</ProtectedRoute>}
                  />
                ))}

                <Route path="/access-denied" element={<AccessDenied />} />
                <Route
                  path="*"
                  element={
                    <h3
                      style={{
                        textAlign: "center",
                        marginTop: "24px",
                      }}
                    >
                      Page Not Found
                    </h3>
                  }
                />
              </Route>
            </Routes>
          </PageStoreProvider>
        </MessageProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
